import React from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import RadioButtonUncheckedRounded from '@mui/icons-material/RadioButtonUncheckedRounded';
import { useTranslation } from 'react-i18next';

import { azure, emerald, line } from '../theme';

// The Fawtara rollout, as a picture.
//
// Every competitor writes a paragraph about the mandate. A visitor scanning a
// page does not read paragraphs — but they will find their own business on a
// timeline in two seconds, and the phase that contains them is the single most
// useful fact this site can give them.
//
// The last marker is emphasised because 1 October 2027 is where almost every
// visitor to this page actually sits. Nothing here is invented: both dates are
// Decision No. 189/2026 (Official Gazette No. 1660, 9 August 2026) and the
// source line says so. The 2028 government marker was removed on 2026-09-13:
// no official source dates it, which the Fawtara page itself already said.
const PHASES = [
  { key: 'p1', done: true },
  { key: 'p2', done: false },
  { key: 'p3', done: false, you: true },
];

export default function NizroTimeline() {
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        p: { xs: 2.5, md: 3.5 },
        borderRadius: 3,
        border: `1px solid ${line}`,
        bgcolor: alpha(azure[500], 0.04),
      }}
    >
      <Typography variant="overline" sx={{ color: azure[300], letterSpacing: '.08em' }}>
        {t('nizro.timeline.eyebrow')}
      </Typography>
      <Typography variant="h6" sx={{ fontWeight: 700, mt: 0.5, mb: 3 }}>
        {t('nizro.timeline.title')}
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gap: { xs: 2, md: 0 },
          gridTemplateColumns: { xs: '1fr', md: `repeat(${PHASES.length}, 1fr)` },
          position: 'relative',
        }}
      >
        {/* The rail. Hidden on mobile, where the phases stack instead. */}
        <Box
          aria-hidden
          sx={{
            display: { xs: 'none', md: 'block' },
            position: 'absolute',
            top: 11,
            insetInline: '12%',
            height: 2,
            background: `linear-gradient(90deg, ${emerald[500]}, ${azure[500]} 55%, ${alpha(azure[500], 0.25)})`,
          }}
        />
        {PHASES.map(({ key, done, you }) => (
          <Stack
            key={key}
            spacing={1}
            sx={{
              position: 'relative',
              alignItems: { xs: 'flex-start', md: 'center' },
              textAlign: { xs: 'start', md: 'center' },
              px: { md: 1 },
            }}
          >
            <Box
              sx={{
                display: 'grid',
                placeItems: 'center',
                width: 24,
                height: 24,
                borderRadius: '50%',
                bgcolor: 'background.default',
                color: done ? emerald[400] : you ? azure[300] : 'text.disabled',
                zIndex: 1,
              }}
            >
              {done ? (
                <CheckCircleRounded fontSize="small" />
              ) : (
                <RadioButtonUncheckedRounded fontSize="small" />
              )}
            </Box>
            <Typography variant="body2" sx={{ fontWeight: 800, color: you ? azure[200] : 'text.primary' }}>
              {t(`nizro.timeline.${key}.date`)}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', maxWidth: 190 }}>
              {t(`nizro.timeline.${key}.who`)}
            </Typography>
            {you && (
              <Box
                sx={{
                  mt: 0.5,
                  px: 1.25,
                  py: 0.25,
                  borderRadius: 5,
                  bgcolor: alpha(azure[400], 0.18),
                  color: azure[200],
                  fontSize: 11,
                  fontWeight: 800,
                  letterSpacing: '.04em',
                }}
              >
                {t('nizro.timeline.youAreHere')}
              </Box>
            )}
          </Stack>
        ))}
      </Box>

      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 3 }}>
        {t('nizro.timeline.source')}
      </Typography>
    </Box>
  );
}
